import React, { useEffect, useRef, useState } from 'react';
import {
  useForm,
  SubmitHandler,
  Controller,
  NestedValue,
} from 'react-hook-form';

import { Button } from 'components/Button';
import { Input } from 'components/form/Input';
import { Select } from 'components/Select';
import { TimePicker } from 'components/TimePicker';
import { DatePicker } from 'components/DatePicker';
import {
  Create as CreateStyled,
  Title,
  Period,
  PeriodItem,
  RadioButtons,
  RadioButtonsItem,
  Separator,
  FormGroup,
  FormGroupItem,
  Text,
  Controls,
} from './CreateStyles';
import { useYupValidationResolver } from 'helpers/form';
import { RadioWrapper, Label } from 'components/TimeWindow';
import { Api } from 'api';
import { Control } from 'components';
import { Benchmark } from 'types/Benchmark';
import { DevTool } from '@hookform/devtools';
import { createJobValidationSchema } from './CreateValidationSchema';
import { showSuccess } from 'helpers/showSuccess';
import {
  DayOfMonth,
  JobCreateImmediate,
  JobCreateRecurring,
  JobFrequency,
  JobType,
} from 'types/Job';
import { dateToServer } from 'helpers/formatters/date';
import { RadioInput } from 'components/form/Radio';
import { PLACEHOLDER } from 'helpers/enums';
import { Footer, Spacer } from '../ManageJobsStyles';
import { GetTimeZone } from 'modules/Benchmarks/BenchmarkList/utils';
import { useHistory } from 'react-router-dom';
import { SelectorType } from 'types/Benchmark';
import { ProblemDetails } from 'helpers/problemDetails';
import { useProblemsHandler } from 'modules/BenchmarkForm/hooks/useProblemsHandler';
import { createFilter } from 'react-select';

type Props = {
  id: string | null;
  onClose: () => void;
};

interface IFormInputs {
  recurring: string;
  name: string;
  jobFrequencyId: number;
  time: string;
  benchmarkIds: NestedValue<Array<string>>;
  numberOfQuartersBack: number;
  startDate: Date;
  endDate: Date;
  dayOfTheWeek: number;
  dayOfMonth: number;
  timeZoneId: number;
}

const daysOfMonth = [
  ...(Array.from({ length: 28 }, (_, i) => {
    const val = i + 1;
    return { label: val + '', value: val };
  }) ?? []),
  { label: 'End of Month', value: -1 },
];

const getJobData = (data: any, benchmarks: Array<SelectorType>) => {
  if (data) {
    let time = data.hour > 12 ? data.hour % 12 : data.hour;

    if (time < 10) {
      time = time === 0 ? '12' : `0${time}`;
    }

    const minutes = data.minute < 10 ? `0${data.minute}` : data.minute;

    const benchmarkIds = data.includeAllBenchmarks
      ? benchmarks.map((item: any) => item.value)
      : data.benchmarks.map((item: any) => {
          return item.id;
        });

    return {
      recurring: data.jobType.id === JobType.Immediate ? 'false' : 'true',
      name: data.name,
      jobFrequencyId: data.jobFrequency?.id,
      dayOfTheWeek: data.dayOfTheWeek,
      dayOfMonth: !data.isEndOfMonth ? data.dayOfMonth : DayOfMonth.EndOfMonth,
      time: `${time}:${minutes}:${data.hour >= 12 ? 'PM' : 'AM'}`,
      numberOfQuartersBack: data.numberOfQuartersBack,
      startDate: new Date(data.specificStartDate),
      endDate: new Date(data.specificEndDate),
      benchmarkIds,
    };
  }

  return {};
};

export const Create: React.FC<Props> = ({ id, onClose }) => {
  const resolver = useYupValidationResolver(createJobValidationSchema);
  const [key, setKey] = useState(new Date().getTime());
  const [recurring, setRecurring] = useState(true);
  const [benchmarks, setBenchmarks] = useState<Array<SelectorType>>([]);
  const [problems, setProblems] = useState<ProblemDetails>();
  const selectedBenchmarks = useRef<Array<any>>([]);
  const history = useHistory();
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    setError,
  } = useForm<IFormInputs>({
    resolver,
    defaultValues: {
      recurring: 'true',
    },
  });

  useProblemsHandler(setError, problems);

  const { data: jobFrequencies } = Api.lookup.GetJobFrequencies();
  const { data: benchmarksData } = Api.benchmarks.GetBenchmarks();
  const { data: daysOfWeek } = Api.lookup.GetDaysOfWeek();
  const { mutate, isSuccess, isLoading } = Api.jobs.CreateJob();
  const {
    mutate: updateJob,
    isSuccess: isSuccessUpdate,
    isLoading: isLoadingUpdate,
  } = Api.jobs.UpdateJob(id);
  const { refetch, data }: any = Api.jobs.GetJob(id, { enabled: false });

  const jobFrequency = watch('jobFrequencyId');

  useEffect(() => {
    setBenchmarks([
      { label: 'All', value: 'all' },
      ...(benchmarksData?.map((benchmark: Benchmark) => {
        return { value: benchmark.id, label: benchmark.name };
      }) ?? []),
    ] as Array<SelectorType>);
  }, [benchmarksData]);

  useEffect(() => {
    if (isSuccess) {
      showSuccess('Job saved successfully');
      reset();
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, reset]);

  useEffect(() => {
    if (isSuccessUpdate) {
      showSuccess('Job updated successfully');
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessUpdate, reset]);

  useEffect(() => {
    setKey(new Date().getTime());
  }, [recurring, reset]);

  const onSubmit: SubmitHandler<IFormInputs> = (data) => {
    const allBenchmarks = data.benchmarkIds.includes('all');
    if (data.recurring) {
      const timeSplit = data.time.split(':');
      let hour = Number(timeSplit[0]);
      const minute = Number(timeSplit[1]);
      const ampm = timeSplit[2];
      if (ampm === 'PM' && hour < 12) hour += 12;
      if (ampm === 'AM' && hour === 12) hour -= 12;
      let body: JobCreateRecurring = {
        jobTypeId: JobType.Recurring,
        jobFrequencyId: data.jobFrequencyId,
        includeAllBenchmarks: allBenchmarks,
        benchmarkIds: allBenchmarks ? [] : data.benchmarkIds,
        name: data.name,
        hour: hour,
        minute: minute,
        numberOfQuartersBack: data.numberOfQuartersBack,
        timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      if (data.jobFrequencyId === JobFrequency.Weekly) {
        body.dayOfTheWeek = data.dayOfTheWeek;
      } else if (
        data.jobFrequencyId === JobFrequency.Monthly &&
        data.dayOfMonth === DayOfMonth.EndOfMonth
      ) {
        body.isEndOfMonth = true;
      } else if (data.jobFrequencyId === JobFrequency.Monthly) {
        body.dayOfMonth = data.dayOfMonth;
        body.isEndOfMonth = false;
      }

      if (id) {
        updateJob(body, {
          onError: (data: any) => {
            setProblems(new ProblemDetails(data.response.data));
          },
        });
      } else {
        mutate(body, {
          onError: (data: any) => {
            setProblems(new ProblemDetails(data.response.data));
          },
        });
      }
    } else {
      let body: JobCreateImmediate = {
        jobTypeId: JobType.Immediate,
        name: data.name || null,
        includeAllBenchmarks: allBenchmarks,
        benchmarkIds: allBenchmarks ? [] : data.benchmarkIds,
        specificStartDate: dateToServer(new Date(data.startDate)),
        specificEndDate: dateToServer(new Date(data.endDate)),
      };

      if (id) {
        updateJob(body);
      } else {
        mutate(body, {
          onError: (data: any) => {
            setProblems(new ProblemDetails(data.response.data));
          },
        });
      }
    }
  };

  function getTimeValue(time: string) {
    if (time) {
      const timeSplit = time.split(':');
      const h = timeSplit[0];
      const m = timeSplit[1];
      const period = timeSplit[2];
      return { h, m, period };
    } else {
      return { h: '', m: '', period: '' };
    }
  }

  function handleSelection(val: any, onChange: any) {
    const lastSelection = val[val.length - 1];
    if (benchmarks.length - val.length === 1) {
      let difference = selectedBenchmarks.current.filter(
        (x) => val.findIndex((y: any) => y.value === x) === -1
      );
      if (difference.length === 1 && difference[0] === 'all') {
        onChange([]);
        selectedBenchmarks.current = [];
        return;
      } else if (difference.length === 0) {
        onChange(benchmarks.map((b: any) => b.value));
        selectedBenchmarks.current = benchmarks.map((b: any) => b.value);
        return;
      }
    }

    let benchmarkSelections: Array<any> = [];
    if (lastSelection?.value === 'all') {
      benchmarkSelections = benchmarks.map((b: any) => b.value);
    } else if (val.length < benchmarks.length) {
      const allIndex = val.findIndex((v: any) => v.value === 'all');
      if (allIndex > -1) {
        val.splice(allIndex, 1);
      }
      benchmarkSelections = val.map((v: any) => v.value);
    } else {
      benchmarkSelections = val.map((v: any) => v.value);
    }

    onChange(benchmarkSelections);
    selectedBenchmarks.current = benchmarkSelections;
  }

  useEffect(() => {
    if (id) {
      refetch();
    }
  }, [id, refetch]);

  useEffect(() => {
    if (data && id && benchmarks) {
      setRecurring(data.data.jobType.id === JobType.Immediate ? false : true);
      const jobData = getJobData(data.data, benchmarks);
      reset(jobData);
      selectedBenchmarks.current = jobData.benchmarkIds ?? [];
    }
  }, [data, id, reset, benchmarks]);

  const handleChangeFrequency = (value: number, onChange: any) => {
    setValue('dayOfTheWeek', 0);
    setValue('dayOfMonth', 0);
    onChange(value);
  };

  useEffect(() => {
    if (history.location.state) {
      const { recurring } = history.location.state as { recurring: boolean };
      reset({
        recurring:
          recurring === undefined ? 'true' : recurring ? 'true' : 'false',
      });
      setRecurring(recurring ?? true);
    }
  }, [history]);

  const filterOptions = {
    stringify: (option: any) => `${option.label}`,
  };

  return (
    <CreateStyled key={key}>
      {!id && (
        <RadioButtons>
          <RadioButtonsItem>
            <RadioWrapper>
              <RadioInput
                register={register('recurring', {
                  required: true,
                })}
                value='true'
                onClick={() => {
                  setRecurring(true);
                  reset({ recurring: 'true' });
                }}
              />
              <Label>Recurring</Label>
            </RadioWrapper>
          </RadioButtonsItem>
          <RadioButtonsItem>
            <RadioWrapper>
              <RadioInput
                register={register('recurring', {
                  required: true,
                })}
                value='false'
                onClick={() => {
                  setRecurring(false);
                  reset({ recurring: 'false' });
                }}
              />
              <Label>Immediate</Label>
            </RadioWrapper>
          </RadioButtonsItem>
        </RadioButtons>
      )}
      <form onSubmit={handleSubmit(onSubmit)} style={{ marginTop: '20px' }}>
        {recurring ? (
          <>
            <FormGroup style={{ marginBottom: '30px' }}>
              <Controls style={{ marginTop: 0 }}>
                <Control label='Name of Job' required>
                  <Input
                    {...register('name')}
                    placeholder={PLACEHOLDER.enterJobName}
                    errorMessage={errors.name && errors.name.message}
                    style={{ width: 270 }}
                    data-testid='name-input'
                  />
                </Control>
                <Control label='Frequency' required>
                  <Controller
                    control={control}
                    name='jobFrequencyId'
                    render={({ field: { onChange, value } }) => {
                      return (
                        <Select
                          placeholder='Select'
                          options={jobFrequencies || []}
                          onChange={(val) =>
                            handleChangeFrequency(val.value, onChange)
                          }
                          value={[...(jobFrequencies || [])].filter(
                            (option: any) => option.value === value
                          )}
                          errorMessage={
                            errors.jobFrequencyId &&
                            errors.jobFrequencyId.message
                          }
                          style={{ width: 150 }}
                          testid='job-frequency-select'
                        />
                      );
                    }}
                  />
                </Control>
                {jobFrequency === JobFrequency.Weekly && (
                  <Control label='Day of Week' required>
                    <Controller
                      control={control}
                      name='dayOfTheWeek'
                      render={({ field: { onChange, value } }) => {
                        return (
                          <Select
                            placeholder='Select'
                            options={daysOfWeek || []}
                            onChange={(val) => onChange(val.value)}
                            value={[...(daysOfWeek || [])].filter(
                              (option: any) => option.value === value
                            )}
                            errorMessage={
                              errors.dayOfTheWeek && errors.dayOfTheWeek.message
                            }
                            style={{ width: 150 }}
                            testid='day-of-week-select'
                          />
                        );
                      }}
                    />
                  </Control>
                )}
                {jobFrequency === JobFrequency.Monthly && (
                  <Control label='Day of Month' required>
                    <Controller
                      control={control}
                      name='dayOfMonth'
                      render={({ field: { onChange, value } }) => {
                        return (
                          <Select
                            placeholder='Select'
                            options={daysOfMonth || []}
                            onChange={(val) => onChange(val.value)}
                            value={[...(daysOfMonth || [])].filter(
                              (option: any) => option.value === value
                            )}
                            errorMessage={
                              errors.dayOfMonth && errors.dayOfMonth.message
                            }
                            style={{ width: 150 }}
                            testid='day-of-month-select'
                          />
                        );
                      }}
                    />
                  </Control>
                )}
                <Controller
                  control={control}
                  name='time'
                  render={({ field: { onChange, value } }) => {
                    return (
                      <TimePicker
                        onChange={(val) => {
                          onChange(`${val.h}:${val.m}:${val.period}`);
                        }}
                        label='Time'
                        info={GetTimeZone()}
                        required
                        hasError={
                          errors.time && errors.time.message ? true : false
                        }
                        errorMessage={errors.time && errors.time.message}
                        value={getTimeValue(value)}
                        testid='time'
                      />
                    );
                  }}
                />
              </Controls>
            </FormGroup>
            <Separator />
            <Title style={{ margin: '12px 0 15px 0' }}>
              Benchmark Calculation Period
            </Title>
            <Controls>
              <Control label='Benchmarks' required>
                <Controller
                  control={control}
                  name='benchmarkIds'
                  render={({ field: { onChange, value } }) => {
                    return (
                      <Select
                        placeholder='Select'
                        options={benchmarks || []}
                        onChange={(val, item) => {
                          handleSelection(val, onChange);
                        }}
                        value={[...(benchmarks || [])].filter((option: any) =>
                          value?.includes(option.value)
                        )}
                        errorMessage={
                          errors.benchmarkIds && errors.benchmarkIds.message
                        }
                        style={{ width: 270, marginBottom: 15 }}
                        withCheckboxes={true}
                        testid='benchmarks-select'
                        filterOption={createFilter(filterOptions)}
                      />
                    );
                  }}
                />
              </Control>
            </Controls>
            <Period>
              <PeriodItem>
                <Title style={{ marginBottom: 18 }}>Start Period</Title>
                <Control label='Rolling Time Period (quarters)' required>
                  <Input
                    {...register('numberOfQuartersBack')}
                    placeholder='Enter number'
                    errorMessage={
                      errors.numberOfQuartersBack &&
                      errors.numberOfQuartersBack.message
                    }
                    style={{ width: 120 }}
                    data-testid='number-quarters-back-input'
                  />
                </Control>
              </PeriodItem>
              <PeriodItem>
                <Title style={{ marginBottom: 18 }}>End Period</Title>
                <Text>No end date.</Text>
              </PeriodItem>
            </Period>
          </>
        ) : (
          <>
            <FormGroup style={{ marginBottom: '30px' }}>
              <FormGroupItem size='lg'>
                <Control label='Name of Job (optional)'>
                  <Input
                    {...register('name')}
                    placeholder={PLACEHOLDER.enterJobName}
                    errorMessage={errors.name && errors.name.message}
                    style={{ width: 270, marginTop: 6 }}
                    data-testid='name'
                  />
                </Control>
              </FormGroupItem>
            </FormGroup>
            <Separator />
            <Title style={{ margin: '12px 0 15px 0' }}>
              Benchmark Calculation Period
            </Title>
            <Controls style={{ marginTop: 0 }}>
              <Control label='Benchmarks' required>
                <Controller
                  control={control}
                  name='benchmarkIds'
                  render={({ field: { onChange, value } }) => {
                    return (
                      <Select
                        placeholder='Select'
                        options={benchmarks || []}
                        onChange={(val, item) => {
                          handleSelection(val, onChange);
                        }}
                        value={[...(benchmarks || [])].filter((option: any) =>
                          value?.includes(option.value)
                        )}
                        errorMessage={
                          errors.benchmarkIds && errors.benchmarkIds.message
                        }
                        style={{ width: 270, marginBottom: 15 }}
                        withCheckboxes={true}
                        testid='benchmarks-select'
                        filterOption={createFilter(filterOptions)}
                      />
                    );
                  }}
                />
              </Control>
              <Control label='Start Date' required>
                <Controller
                  control={control}
                  name='startDate'
                  render={({ field: { onChange, value } }) => {
                    return (
                      <DatePicker
                        onChange={onChange}
                        selected={value}
                        errorMessage={
                          errors.startDate && errors.startDate.message
                        }
                        style={{ width: 160 }}
                        testid='start-date-picker'
                      />
                    );
                  }}
                />
              </Control>
              <Control label='End Date' required>
                <Controller
                  control={control}
                  name='endDate'
                  render={({ field: { onChange, value } }) => {
                    return (
                      <DatePicker
                        onChange={onChange}
                        selected={value}
                        errorMessage={errors.endDate && errors.endDate.message}
                        style={{ width: 160 }}
                        testid='end-date-picker'
                      />
                    );
                  }}
                />
              </Control>
            </Controls>
          </>
        )}
        <div style={{ flex: 1 }}></div>
        <Footer>
          <Spacer />
          <Button
            onClick={onClose}
            variant='secondary'
            data-testid='cancel-button'
          >
            Cancel
          </Button>
          <Button
            type='submit'
            loading={isLoading || isLoadingUpdate}
            disabled={isLoading || isLoadingUpdate}
            data-testid='submit-button'
          >
            Submit
          </Button>
        </Footer>
      </form>
      <DevTool control={control} />
    </CreateStyled>
  );
};
